import { useNavigate } from "react-router";
import { useGlobalContext } from "../../stores/Global";
import { FormEvent, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import FormInput from "../molecules/FormInput";
import { Link } from "react-router-dom";
import { AuthService, UserService } from "../../services/api";
import ActivateAccountDialog from "./ActivateAccountDialog";
import EmailConfirmationDialog from "./EmailConfirmationDialog";
import OTPDialog from "./OTPDialog";
import { useTranslation } from "react-i18next";
import { UpdateUserLanguageLanguageEnum } from "../../services/swagger";
import { startServiceWorker } from "../../workers/main";
import { useMutation } from "@tanstack/react-query";

export default function LoginForm() {
  const { t } = useTranslation("LoginForm");
  const { context, setContext } = useGlobalContext();
  const navigate = useNavigate();
  const [activateDialog, setActivateDialog] = useState(false);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [otpDialog, setOtpDialog] = useState(false);
  const [emailConfirmationDialog, setEmailConfirmationDialog] = useState(false);

  const loginMutationFn = async () => {
    const res = await AuthService.login({ loginBody: { login, password } });
    return res?.data;
  };

  const { mutate: initiateLogin, isPending } = useMutation({
    mutationFn: loginMutationFn,
    onSettled: async (data) => {
      if (data?.data?.mustActivate) {
        setActivateDialog(true);
        return;
      } else if (data?.success) {
        if (data?.data?.emailConfirmationRequired) {
          setEmailConfirmationDialog(true);
          return;
        }
      }
      localStorage.setItem("authToken", data?.data?.token || "");
      if (data?.data?.otpRequired) {
        setOtpDialog(true);
        return;
      }
      await UserService.updateUserLanguage({
        language: context.language as UpdateUserLanguageLanguageEnum,
      });
      startServiceWorker();
      setContext({ ...context, user: data?.data?.user });
      navigate("/");
    },
  });

  const handleLogin = async (e?: FormEvent) => {
    e?.preventDefault();
    initiateLogin();
  };

  const handleCloseActivateDialog = (success: boolean) => {
    setActivateDialog(false);
    if (success) handleLogin();
  };

  return (
    <Paper
      sx={{
        bgcolor: "background.default",
        pt: 2,
        p: { xs: 4, md: 8 },
        maxWidth: { xs: 350, sm: 500, md: 560 },
        minWidth: { xs: 300, sm: 450, md: 560 },
        borderRadius: { xs: 0, md: 8 },
      }}
      component="form"
      onSubmit={handleLogin}
    >
      <EmailConfirmationDialog
        handleOTP={() => setOtpDialog(true)}
        handleClose={() => setEmailConfirmationDialog(false)}
        login={login}
        password={password}
        open={emailConfirmationDialog}
      />
      <OTPDialog handleClose={() => setOtpDialog(false)} open={otpDialog} />
      <ActivateAccountDialog
        open={activateDialog}
        handleClose={handleCloseActivateDialog}
      />
      <Typography color="primary" variant="h2" fontWeight="bold" fontSize={32}>
        {t("Header")}
      </Typography>
      <Typography fontWeight="light" mb={4}>
        {t("Subheader")}
      </Typography>
      <Stack spacing={1}>
        <FormInput
          placeholder={t("LoginInput.placeholder")}
          type="string"
          required
          field="login"
          label={t("LoginInput.label")}
          value={login}
          onChange={(v) => setLogin(v as string)}
        />
        <FormInput
          inputType="password"
          type="string"
          required
          field="password"
          label={t("Password")}
          placeholder={t("Password")}
          value={password}
          onChange={(v) => setPassword(v as string)}
        />
        <Box textAlign="right">
          <Link to="/account/forgot-password">{t("ForgotPassword")}</Link>
        </Box>
      </Stack>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 2, borderRadius: 12, height: 50, mb: 2 }}
        loading={isPending}
      >
        {t("Login")}
      </Button>
      <Box>
        <Typography fontWeight="light">
          {t("DontHaveAccount")}{" "}
          <Typography
            onClick={() => navigate("/register")}
            color="primary"
            component="span"
            fontSize={16}
            ml={4}
            sx={{ cursor: "pointer" }}
          >
            <u>{t("Register")}</u>
          </Typography>
        </Typography>
      </Box>
    </Paper>
  );
}
